import type { JSX } from 'react';
import { Card, CardContent, Grid, Typography, Chip, Stack, Divider } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { AsyncSection } from '../components/AsyncSection.js';

interface OverviewDto {
  generatedAt: string;
  circles: { total: number; active: number };
  consents: { active: number; pending: number };
  jobs: { queued: number; failed: number };
  users: { total: number };
  outbox: { pending: number };
  usage: {
    transcriptionRunsToday: number;
    estimatedCostCentsToday: number;
    observedCostCentsToday: number;
    costIsExact: boolean;
  };
  features: Array<{ name: string; enabled?: boolean; killSwitch?: boolean; provider?: string; configured?: boolean }>;
}

function formatCents(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function OverviewPage(): JSX.Element {
  const query = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => api.get<OverviewDto>('/v1/admin/overview'),
    refetchInterval: 30_000,
  });

  return (
    <>
      <Typography variant="h1" gutterBottom>
        Visão geral
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Métricas agregadas e pseudônimas. Custo estimado pode não ser exato porque nem todo
        provedor informa o valor consumido.
      </Typography>
      <AsyncSection query={query} emptyMessage="Sem dados agregados ainda.">
        {(data) => (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Círculos
                  </Typography>
                  <Typography variant="h1">{data.circles.total}</Typography>
                  <Typography variant="body2">{data.circles.active} ativos</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Consentimentos
                  </Typography>
                  <Typography variant="h1">{data.consents.active}</Typography>
                  <Typography variant="body2">{data.consents.pending} pendentes</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Jobs
                  </Typography>
                  <Typography variant="h1">{data.jobs.queued}</Typography>
                  <Typography variant="body2">
                    na fila · {data.jobs.failed} com falha · {data.outbox.pending} eventos pendentes
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Custo de IA (24 h)
                  </Typography>
                  <Typography variant="h1">{formatCents(data.usage.observedCostCentsToday)}</Typography>
                  <Typography variant="body2">
                    estimado {formatCents(data.usage.estimatedCostCentsToday)}
                    {data.usage.costIsExact ? '' : ' (valor observado pode estar incompleto)'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h2" gutterBottom>
                    Estado das features
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {data.features.map((feature) => (
                      <Chip
                        key={feature.name}
                        label={
                          feature.name === 'ai'
                            ? `IA: ${feature.enabled ? 'habilitada' : 'desabilitada'}${feature.killSwitch ? ' (kill switch ativo)' : ''}`
                            : feature.name === 'billing'
                              ? `Cobrança: ${feature.provider}`
                              : `Antivírus: ${feature.configured ? 'configurado' : 'não configurado'}`
                        }
                        color={
                          feature.name === 'ai'
                            ? feature.killSwitch
                              ? 'error'
                              : feature.enabled
                                ? 'success'
                                : 'default'
                            : feature.configured
                              ? 'success'
                              : 'warning'
                        }
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" color="text.secondary">
                    Gerado em {new Date(data.generatedAt).toLocaleString('pt-BR')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </AsyncSection>
    </>
  );
}